/**
 * @fileoverview チュートリアル向けガード
 */

const DEFAULT_ACTION_STATE = Object.freeze({
  start: false,
  menu: false,
  skill: false,
  boardMatch: false,
});

let tutorialMeta = {
  status: 'idle',
  step: null,
};

let actionPermissions = { ...DEFAULT_ACTION_STATE };

const actionCallbacks = new Map();
let swapConstraints = null;

function normalizePoint(point) {
  if (!point || typeof point !== 'object') return null;
  const row = Number(point.row);
  const col = Number(point.col);
  if (!Number.isInteger(row) || !Number.isInteger(col)) {
    return null;
  }
  return { row, col };
}

function pointsEqual(a, b) {
  return Boolean(a) && Boolean(b) && a.row === b.row && a.col === b.col;
}

function normalizeSwapConstraints(constraints) {
  if (!Array.isArray(constraints) || constraints.length === 0) {
    return null;
  }
  const normalized = constraints
    .map((constraint) => {
      if (!constraint) return null;
      const from = normalizePoint(constraint.from);
      const to = normalizePoint(constraint.to);
      if (!from || !to) return null;
      const allowReverse = Boolean(constraint.allowReverse);
      return { from, to, allowReverse };
    })
    .filter(Boolean);
  return normalized.length > 0 ? normalized : null;
}

export function setSwapConstraints(constraints) {
  swapConstraints = normalizeSwapConstraints(constraints);
}

export function shouldBlockSwap(from, to) {
  if (!isRunning()) {
    return false;
  }
  if (!swapConstraints) {
    return false;
  }

  const normalizedFrom = normalizePoint(from);
  const normalizedTo = normalizePoint(to);

  if (!normalizedFrom || !normalizedTo) {
    return false;
  }

  const isAllowed = swapConstraints.some((constraint) => {
    const fromMatch = pointsEqual(constraint.from, normalizedFrom);
    const toMatch = pointsEqual(constraint.to, normalizedTo);
    const directMatch = fromMatch && toMatch;

    const reverseFromMatch = constraint.allowReverse && pointsEqual(constraint.from, normalizedTo);
    const reverseToMatch = constraint.allowReverse && pointsEqual(constraint.to, normalizedFrom);
    const reverseMatch = reverseFromMatch && reverseToMatch;

    return directMatch || reverseMatch;
  });

  return !isAllowed;
}

function cloneActionState(state) {
  return {
    start: Boolean(state.start),
    menu: Boolean(state.menu),
    skill: Boolean(state.skill),
    boardMatch: Boolean(state.boardMatch),
  };
}

export function registerActionCallbacks(callbacks = {}) {
  ['start', 'menu', 'skill', 'boardMatch'].forEach((key) => {
    const fn = callbacks[key];
    if (fn === null) {
      actionCallbacks.delete(key);
    } else if (typeof fn === 'function') {
      actionCallbacks.set(key, fn);
    }
  });
}

export function updateGuardState({ status, step, allowedActions = [] } = {}) {
  tutorialMeta = {
    status: status ?? tutorialMeta.status,
    step: step ?? tutorialMeta.step,
  };

  const nextPermissions = { ...DEFAULT_ACTION_STATE };
  allowedActions.forEach((action) => {
    if (action in nextPermissions) {
      nextPermissions[action] = true;
    }
  });
  actionPermissions = nextPermissions;
}

function isRunning() {
  return tutorialMeta.status === 'running';
}

function isAllowed(action) {
  if (!isRunning()) return true;
  return Boolean(actionPermissions[action]);
}

function notify(action, details) {
  if (!isRunning()) return;
  const callback = actionCallbacks.get(action);
  if (typeof callback === 'function') {
    callback({ ...tutorialMeta, action, details });
  }
}

export function shouldBlockStart() {
  return !isAllowed('start');
}

export function notifyStartClicked() {
  notify('start');
}

export function shouldBlockMenuOpen() {
  return !isAllowed('menu');
}

export function notifyMenuOpened() {
  notify('menu');
}

export function shouldBlockSkillUse() {
  return !isAllowed('skill');
}

export function notifySkillUsed() {
  notify('skill');
}

export function notifyBoardMatched(details) {
  notify('boardMatch', details);
}

export function getCurrentTutorialStep() {
  return tutorialMeta.step;
}

export function getActionStateSnapshot() {
  return cloneActionState(actionPermissions);
}
